import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getModelById, getModelWorkspacesByModelId, ModelRecordWithProviderName } from "@/db/models"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.models', supabase)

type Row = Tables<'models'>
type WsRow = Tables<'model_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const modelWorkspaces = await getModelWorkspacesByModelId(supabase, id)
	return modelWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setModels
 * @returns
 */
export function makeModelChangeHandler(
	setModels: Dispatch<SetStateAction<ModelRecordWithProviderName[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `model-changes-${uuid}`,
		channelName: `model-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'models',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const model = payload.new as Row
				const modelWithProvider = await getModelById(supabase, model.id)
				//here not checking for workspace since will update only if found and it avoid async db call
				setModels(prev => {
					const exists = prev.some(p => p.id === model.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling model update:${printObject(model)}`)
					return prev.map(p => p.id === model.id ? modelWithProvider : p)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setModels
 * @returns
 */
export function makeModelWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setModels: Dispatch<SetStateAction<ModelRecordWithProviderName[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `model-ws-changes-${uuid}`,
		channelName: `model-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'model_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const modelWs = payload.new as WsRow
				const id = modelWs.model_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const modelWithProvider = await getModelById(supabase, id)
					if (modelWithProvider) {
						setModels(prev => {
							if (prev.some(p => p.id === modelWithProvider.id)) return prev
							logger.info(`Handling model Insert:${printObject(modelWithProvider)}`)
							return [...prev, modelWithProvider]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const modelWs = payload.old as WsRow
				const id = modelWs.model_id
				setModels(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling model Delete:${id}`)
					return prev.filter((p: any) => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}